import React, { useState, useEffect, useRef } from 'react';
import { X, Download, ExternalLink, Check, Loader2, Code2, Eye, NotebookPen } from 'lucide-react';
import Tip from '../ui/tip';
import FileEditor from '../Editor';
import Preview from '../Preview';
import { XmlPreviewCompact, isXmlFile } from '../XmlViewer';
import { hasPreview, getPreviewKind, PREVIEW_KIND } from '../preview/previewKind';
import { isDraft } from './fileMeta';
import { useDraftDock } from '../../context/DraftDockContext';
import { cn } from '../../lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * FilePanel — the body of an opened artifact.
 *
 * One component serves both desktop and mobile inside ArtifactSurface. It
 * defaults to the rendered Preview for previewable artifacts (Markdown, UI
 * mockups, HTML, XML, assets) and provides an instant Preview / Code toggle in
 * the header so users don't have to leave the Artifacts tab just to view or
 * edit a file.
 */
export function FilePanel({
    file,
    projectId,
    isSaving = false,
    onContentChange,
    onClose,
    onDownload,
    onOpenInEditor,
    getIcon,
    readOnly = false,
}) {
    const isMobile = useIsMobile();
    const draftDock = useDraftDock();
    const editorRef = useRef(null);
    const pendingScrollLine = useRef(null);

    const previewable = hasPreview(file);
    const isImage = file?.type === 'asset' && file?.content?.startsWith('data:image');
    const isEditable = !isImage && !readOnly;

    // Default to preview when the artifact has a rendered form; otherwise open
    // straight in the code editor.
    const [mode, setMode] = useState(() => (previewable ? 'preview' : 'edit'));

    // Reset to the default mode whenever the user selects a different artifact.
    useEffect(() => {
        if (!file?.id) return;
        setMode(hasPreview(file) ? 'preview' : 'edit');
        // eslint-disable-next-line
    }, [file?.id]);

    // If the user clicked "Edit in Source" inside a Markdown preview, scroll
    // Monaco to that line once the editor mounts.
    useEffect(() => {
        if (mode === 'edit' && pendingScrollLine.current && editorRef.current) {
            const line = pendingScrollLine.current;
            pendingScrollLine.current = null;
            editorRef.current.revealLineInCenter(line, 0);
        }
    }, [mode]);

    if (!file) return null;

    // A draft opened from the artifact list is still a draft — Monaco can edit
    // it, but the columns and live preview it was written in are one click away.
    const canOpenInDock = !readOnly && draftDock?.enabled && isDraft(file);
    const isXml = isXmlFile(file.name);
    const previewKind = getPreviewKind(file);
    const canToggleMode = previewable && isEditable;
    const showingPreview = mode === 'preview' && previewable;

    const handleSyncEditor = (lineNumber) => {
        if (!isEditable) return;
        pendingScrollLine.current = lineNumber;
        setMode('edit');
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            {/* ─── Header ──────────────────────────────────────────────────
                Actions live up here rather than in a footer: this panel is
                full height, so a footer would permanently cost ~56px of the
                document you came to read. */}
            <div className={cn(
                '@container/panelheader flex flex-shrink-0 items-center justify-between gap-2 border-b border-white/10 bg-black/40 px-4 py-3 @[30rem]/panelheader:gap-3',
                isMobile && 'pt-safe-top'
            )}>
                <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-secondary/40 [&_svg]:h-4 [&_svg]:w-4">
                        {getIcon?.(file.type, file.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="truncate font-mono text-sm font-semibold">{file.name}</h3>
                        <div className="flex min-w-0 items-center gap-2 overflow-hidden text-xs">
                            <span className="truncate rounded-md border border-white/10 bg-secondary/40 px-1.5 py-0.5 text-muted-foreground">
                                {file.category}
                            </span>
                            {!isEditable ? (
                                <span className="flex-shrink-0 text-muted-foreground">Preview</span>
                            ) : isSaving ? (
                                <span className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-muted-foreground">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    <span className="hidden @[26rem]/panelheader:inline">Saving…</span>
                                </span>
                            ) : (
                                <span className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-green-400/70">
                                    <Check className="h-3 w-3" />
                                    <span className="hidden @[26rem]/panelheader:inline">Saved</span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-shrink-0 items-center gap-1">
                    {canOpenInDock ? (
                        <Tip label="Open in scratchpad">
                            <button
                                onClick={() => draftDock.open(file.id)}
                                data-testid="file-panel-open-scratchpad"
                                className="rounded-lg p-2 text-primary transition-colors hover:bg-primary/10"
                            >
                                <NotebookPen className="h-4 w-4" />
                            </button>
                        </Tip>
                    ) : null}

                    {canToggleMode ? (
                        <div
                            role="group"
                            aria-label="View mode"
                            className="mr-1 flex items-center rounded-lg border border-white/10 bg-white/5 p-0.5"
                        >
                            <Tip label="Preview">
                                <button
                                    type="button"
                                    onClick={() => setMode('preview')}
                                    aria-pressed={showingPreview}
                                    data-testid="file-panel-mode-preview"
                                    className={cn(
                                        'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                                        showingPreview
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                    <span className="hidden @[32rem]/panelheader:inline">Preview</span>
                                </button>
                            </Tip>
                            <Tip label="Edit code">
                                <button
                                    type="button"
                                    onClick={() => setMode('edit')}
                                    aria-pressed={!showingPreview}
                                    data-testid="file-panel-mode-edit"
                                    className={cn(
                                        'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                                        !showingPreview
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    <Code2 className="h-3.5 w-3.5" />
                                    <span className="hidden @[32rem]/panelheader:inline">Code</span>
                                </button>
                            </Tip>
                        </div>
                    ) : null}

                    <Tip label="Download">
                        <button
                            onClick={onDownload}
                            data-testid="file-panel-download"
                            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                        >
                            <Download className="h-4 w-4" />
                        </button>
                    </Tip>

                    {!isMobile && onOpenInEditor ? (
                        <Tip label="Open in full editor">
                            <button
                                onClick={onOpenInEditor}
                                data-testid="file-panel-open-editor"
                                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                            >
                                <ExternalLink className="h-4 w-4" />
                            </button>
                        </Tip>
                    ) : null}

                    <Tip label="Close" hint="Esc">
                        <button
                            onClick={onClose}
                            data-testid="file-panel-close"
                            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </Tip>
                </div>
            </div>

            {/* ─── Body ────────────────────────────────────────────────────
                Monaco's own theme background lives on this wrapper, so the
                header above stays glass and the editor reads as a solid inset. */}
            <div className="min-h-0 flex-1 overflow-hidden bg-[#1e1e1e]">
                {isImage ? (
                    <div className="flex h-full items-center justify-center p-4">
                        <img
                            src={file.content}
                            alt={file.name}
                            className="max-h-full max-w-full rounded-lg object-contain"
                        />
                    </div>
                ) : showingPreview ? (
                    isXml && previewKind === PREVIEW_KIND.XML ? (
                        <XmlPreviewCompact content={file.content || ''} fileName={file.name} />
                    ) : (
                        <Preview
                            file={file}
                            projectId={projectId}
                            onSyncEditor={isEditable ? handleSyncEditor : undefined}
                            readOnly={readOnly}
                            hideDocToolbar
                        />
                    )
                ) : isEditable ? (
                    <FileEditor
                        ref={editorRef}
                        // Remount on file change so Monaco builds a fresh model
                        // rather than showing the previous file's content.
                        key={file.id}
                        file={file}
                        onChange={(content) => onContentChange?.(file.id, content)}
                    />
                ) : (
                    <pre className="h-full w-full overflow-auto whitespace-pre-wrap p-4 text-left font-mono text-sm">
                        {file.content || 'No content.'}
                    </pre>
                )}
            </div>
        </div>
    );
}

export default FilePanel;
